// ═══════════════════════════════════════════════════════════════════════════════
// Employer — Upcoming tasks (tomorrow … today+7), grouped by date
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { taskInstanceApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { EmployerStatusRibbon } from '../../components/task/employer/EmployerStatusRibbon';
import { APPROVAL_STATUS, TASK_KIND, type TodayTodoItem } from '@fdh/shared';
import { buildEmployerGroupedTaskDateHeading } from '../../utils/employerTaskDateHeading';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';

const ISO_YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

const isIsoYmd = (value: string | null): value is string =>
  Boolean(value && ISO_YMD_RE.test(value));

const getDateInTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
};

const addCalendarDaysToYmd = (ymd: string, deltaDays: number): string => {
  const [yRaw, mRaw, dRaw] = ymd.split('-');
  const y = Number(yRaw);
  const m = Number(mRaw);
  const d = Number(dRaw);
  const base = new Date(y, m - 1, d);
  base.setDate(base.getDate() + deltaDays);
  const yy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const formatMonthDayFromYmd = (dueDateYmd: string): string => {
  const tail = dueDateYmd.slice(5);
  if (tail.length !== 5 || dueDateYmd[4] !== '-') {
    return '';
  }
  return tail;
};

type EmployerUpcomingTaskCardProps = {
  task: TodayTodoItem;
  onOpenDetail: (taskInstanceId: string) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const EmployerUpcomingTaskCard = ({ task, onOpenDetail, t }: EmployerUpcomingTaskCardProps) => {
  const deadlineDisplayText = task.dueTime
    ? task.dueTime
    : formatMonthDayFromYmd(task.dueDate);

  const useGrayDueTimeBox =
    task.status === APPROVAL_STATUS.APPROVED ||
    (task.status === APPROVAL_STATUS.PENDING &&
      Boolean(task.submittedAt && String(task.submittedAt).trim() !== ''));

  const handleOpen = () => {
    onOpenDetail(task.taskInstanceId);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDetail(task.taskInstanceId);
    }
  };

  return (
    <li className="overflow-hidden rounded-xl bg-white shadow-sm dark:bg-gray-900">
      <button
        type="button"
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-label={t('task.employerFuture.openDetailCta', {
          title:
            task.taskKind === TASK_KIND.INVENTORY
              ? `${task.title} (${t('task.list.kindStockTake')})`
              : task.title,
        })}
        className="relative flex w-full items-center gap-3 overflow-hidden rounded-xl p-4 pr-1 text-left transition-colors hover:bg-gray-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-gray-800/40"
      >
        <EmployerStatusRibbon status={task.status} submittedAt={task.submittedAt ?? ''} />
        <div
          className={`flex w-[80px] shrink-0 flex-col items-center justify-center rounded-xl px-2 py-2 ${
            useGrayDueTimeBox
              ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
              : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100'
          }`}
        >
          <p
            className={`whitespace-nowrap font-bold leading-none tracking-tight ${
              task.dueTime ? 'text-[26px] tabular-nums' : 'text-xl'
            }`}
          >
            {deadlineDisplayText}
          </p>
          <p className="mt-1 text-[11px] font-semibold leading-none">{t('helperHome.suggestedTime')}</p>
        </div>
        <div className="min-w-0 flex-1 pr-10">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="truncate font-medium text-gray-900 dark:text-gray-100">{task.title}</h3>
            {task.taskKind === TASK_KIND.INVENTORY ? (
              <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                {t('task.list.kindStockTake')}
              </span>
            ) : null}
          </div>
          {task.description ? (
            <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">{task.description}</p>
          ) : null}
        </div>
        <span
          className="inline-flex w-6 shrink-0 translate-y-2 items-center justify-center self-center text-gray-400 dark:text-gray-500"
          aria-hidden
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </button>
    </li>
  );
};

export const EmployerFutureTasksPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const timezone = resolveDisplayTimeZone(user?.settings.timezone);
  const todayYmd = useMemo(() => getDateInTimezone(timezone), [timezone]);
  const tomorrowYmd = useMemo(() => addCalendarDaysToYmd(todayYmd, 1), [todayYmd]);
  const maxRangeEndYmd = useMemo(() => addCalendarDaysToYmd(todayYmd, 7), [todayYmd]);

  const defaultStart = tomorrowYmd;
  const defaultEnd = maxRangeEndYmd;

  const futStartParam = searchParams.get('futStart');
  const futEndParam = searchParams.get('futEnd');

  const futStartDate = isIsoYmd(futStartParam) ? futStartParam : defaultStart;
  const futEndDate = isIsoYmd(futEndParam) ? futEndParam : defaultEnd;

  const [tasks, setTasks] = useState<TodayTodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!isIsoYmd(next.get('futStart'))) {
      next.set('futStart', defaultStart);
      changed = true;
    }
    if (!isIsoYmd(next.get('futEnd'))) {
      next.set('futEnd', defaultEnd);
      changed = true;
    }
    let start = next.get('futStart') ?? defaultStart;
    let end = next.get('futEnd') ?? defaultEnd;
    if (!isIsoYmd(start)) start = defaultStart;
    if (!isIsoYmd(end)) end = defaultEnd;
    if (start < tomorrowYmd) {
      start = tomorrowYmd;
      next.set('futStart', start);
      changed = true;
    }
    if (end > maxRangeEndYmd) {
      end = maxRangeEndYmd;
      next.set('futEnd', end);
      changed = true;
    }
    if (start > end) {
      next.set('futEnd', start);
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [
    defaultEnd,
    defaultStart,
    maxRangeEndYmd,
    searchParams,
    setSearchParams,
    tomorrowYmd,
  ]);

  const effectiveStart = useMemo(() => {
    const raw = searchParams.get('futStart');
    if (!isIsoYmd(raw)) return futStartDate;
    if (raw < tomorrowYmd) return tomorrowYmd;
    return raw;
  }, [futStartDate, searchParams, tomorrowYmd]);

  const effectiveEnd = useMemo(() => {
    const raw = searchParams.get('futEnd');
    if (!isIsoYmd(raw)) return futEndDate;
    if (raw > maxRangeEndYmd) return maxRangeEndYmd;
    return raw;
  }, [futEndDate, maxRangeEndYmd, searchParams]);

  const clampedStart = effectiveStart > effectiveEnd ? effectiveEnd : effectiveStart;
  const clampedEnd = effectiveStart > effectiveEnd ? effectiveStart : effectiveEnd;

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await taskInstanceApi.employerFuture(clampedStart, clampedEnd);
      if (!response.success || !response.data) {
        setError(response.error ?? t('task.employerFuture.loadError'));
        return;
      }
      setTasks(response.data.tasks ?? []);
    } catch {
      setError(t('task.employerFuture.loadError'));
    } finally {
      setLoading(false);
    }
  }, [clampedEnd, clampedStart, t]);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  const tasksGrouped = useMemo(() => {
    const byDate = new Map<string, TodayTodoItem[]>();
    tasks.forEach((task) => {
      const d = task.dueDate;
      const list = byDate.get(d) ?? [];
      list.push(task);
      byDate.set(d, list);
    });
    const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    return dates.map((date) => ({
      date,
      tasks: (byDate.get(date) ?? []).slice(),
    }));
  }, [tasks]);

  const handleBack = () => {
    navigateBack(navigate, '/tasks');
  };

  const handleBackKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleBack();
    }
  };

  const handleFutStartDateChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('futStart', value);
    const endRaw = next.get('futEnd');
    if (isIsoYmd(endRaw) && value > endRaw) {
      next.set('futEnd', value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleFutEndDateChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('futEnd', value);
    const startRaw = next.get('futStart');
    if (isIsoYmd(startRaw) && value < startRaw) {
      next.set('futStart', value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleOpenDetail = (taskInstanceId: string) => {
    navigate(`/task-instances/${taskInstanceId}`);
  };

  return (
    <div className="min-h-full bg-[#f7f8fb] dark:bg-gray-950">
      <header className="z-10 border-b border-gray-200/80 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              onKeyDown={handleBackKeyDown}
              tabIndex={0}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
              aria-label={t('task.action.back')}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                {t('task.employerFuture.title')}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6 pb-24">
        <div className="mb-4 rounded-xl bg-white p-3 shadow-sm dark:bg-gray-900">
          <div className="flex flex-wrap items-center gap-2">
            <IsoDatePicker
              id="employer-task-future-start-date"
              ariaLabel={t('task.history.startDate')}
              value={clampedStart}
              onChange={handleFutStartDateChange}
              minDate={tomorrowYmd}
              maxDate={clampedEnd || maxRangeEndYmd}
              className="min-w-0 flex-1 basis-[140px]"
              valueFormat="iso"
            />
            <span
              className="shrink-0 px-0.5 text-sm font-medium text-gray-500 dark:text-gray-400"
              aria-hidden
            >
              {t('settlement.helperConfirmDateRangeSeparator')}
            </span>
            <IsoDatePicker
              id="employer-task-future-end-date"
              ariaLabel={t('task.history.endDate')}
              value={clampedEnd}
              onChange={handleFutEndDateChange}
              minDate={clampedStart || tomorrowYmd}
              maxDate={maxRangeEndYmd}
              className="min-w-0 flex-1 basis-[140px]"
              valueFormat="iso"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <div className="rounded-xl bg-red-50 p-6 text-center dark:bg-red-950/40">
            <p className="text-red-600 dark:text-red-300" role="alert">
              {error}
            </p>
            <button
              type="button"
              onClick={() => void fetchTasks()}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none dark:text-red-200"
            >
              {t('task.action.refresh')}
            </button>
          </div>
        ) : tasksGrouped.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
            <p className="text-gray-600 dark:text-gray-300">{t('task.employerFuture.empty')}</p>
          </div>
        ) : (
          <div className="space-y-6" id="employer-future-task-list-anchor">
            {tasksGrouped.map((group) => (
              <section
                key={group.date}
                className="space-y-2"
                aria-labelledby={`employer-future-date-${group.date}`}
              >
                <h2
                  id={`employer-future-date-${group.date}`}
                  className="flex items-baseline justify-between gap-2 text-sm font-semibold tracking-tight text-gray-600 dark:text-gray-300"
                >
                  <span className="min-w-0">
                    {buildEmployerGroupedTaskDateHeading({
                      dateYmd: group.date,
                      todayYmd,
                      tomorrowYmd,
                      timezone,
                      language,
                      t,
                    })}
                  </span>
                  <span className="shrink-0 font-semibold text-emerald-600 dark:text-emerald-400">
                    {t('task.history.groupTotalCount', { count: group.tasks.length })}
                  </span>
                </h2>
                <ul className="space-y-3" role="list">
                  {group.tasks.map((task) => (
                    <EmployerUpcomingTaskCard
                      key={task.taskInstanceId}
                      task={task}
                      onOpenDetail={handleOpenDetail}
                      t={t}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
